import { Client, messageCallbackType } from "@stomp/stompjs"
import { Camera, NO_CAMERA } from "../../../domain/models/interfaces/camera"
import { Microphone, NO_MICROPHONE } from "../../../domain/models/interfaces/microphone"
import { useEffect, useRef, useState } from "react"
import { useGetProfile } from "../components/profile/queries"
import useAuthentication from "./authentication/useAuthentication"
import { acc } from "react-native-reanimated"
import Configuration from "../../../domain/services/ConfigurationService"
import { useCabanaApi } from "../providers/CabanaApiProvider"
import useMeeting, { AudioStream, VideoStream } from "./useMeeting"
import useWebSocket from "./useWebsocket"
import { useUserProfileContext } from "../providers/UserProfileProvider"
import { useMicrophonesContext } from "../providers/MicrophoneProvider"
import { useCamerasContext } from "../providers/CameraProvider"
import useSpeakingDetection from "./useSpeakingDetection"

export type RoomInfo = {
  id: string,
  title: string,
  startTime: Date
}

export type Attendee = {
  id: string
  alias: string
  role: string
  photo: string
  camera: boolean
  screenShare: boolean
  microphone: boolean
  handRaised: boolean
  speaking: boolean
  typing: boolean
  token: string
  screen: string
  connected: boolean
}

export type ChatMessage = {
  alias: string,
  message: string,
  timestamp: Date
}

export type UseRoomResult = UseRoomActions & {
  connected: boolean
  error: string | null

  info: RoomInfo
  me: Attendee
  participants: Attendee[]
  audio: AudioStream[]
  video: VideoStream[]
  messages: ChatMessage[]
}

interface UseRoomActions {
  chooseAlias: (alias: string) => Promise<void>
  join: () => Promise<void>
  leave: () => Promise<void>
  start: () => Promise<void>
  stop: () => Promise<void>
  microphoneOn: () => Promise<void>
  microphoneOff: () => Promise<void>
  cameraOn: () => Promise<void>
  cameraOff: () => Promise<void>
  screenShareOn: () => Promise<void>
  screenShareOff: () => Promise<void>
  handUp: () => Promise<void>
  handDown: () => Promise<void>
  startSpeaking: () => Promise<void>
  stopSpeaking: () => Promise<void>
  startTyping: () => Promise<void>
  stopTyping: () => Promise<void>
  sendMessage: (message: string) => Promise<void>
  //submitFeedback: (feedback: any) => Promise<void>
  changeToParticipant: () => Promise<void>
  changeToSpectator: () => Promise<void>

}

const useRoom = (roomId: string): UseRoomResult => {
  const [roomInfo, setRoomInfo] = useState<RoomInfo>(null)
  const [me, setMe] = useState<Attendee>(null)
  const [participants, setParticipants] = useState<Attendee[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])

  const { connected: socketConnected, error: socketError, connect, disconnect, subscribe} = useWebSocket(Configuration.CABANA_SERVICE_WEBSOCKET_URL)
  const { connected: meetingConnected, error: meetingError, join, leave, setMicrophone, microphoneOff, microphoneOn, setCamera, cameraOff, cameraOn, audio, video } = useMeeting(me?.token)
  const { selectedMicrophone } = useMicrophonesContext()
  const { selectedCamera } = useCamerasContext()
  const { userProfile } = useUserProfileContext()
  const { RoomApiV2 } = useCabanaApi()
  const { isSpeaking } = useSpeakingDetection()

  const onRoomUpdate: (data: any) => void = (data) => {
    setRoomInfo({
      id: data.id,
      title: data.title,
      startTime: data.startTime
    })
    setMe(data.attendees.find(attendee => attendee.id === userProfile.id))
    setParticipants(data.attendees)
  }

  const onChatUpdate: (data: any) => void = (data) => {
    const message = data as ChatMessage
    setMessages(prev => [...prev, message])
  }

  const noop = async () => {}
  const actions: UseRoomActions = me ? {
    join: () => RoomApiV2.join(roomId, me.id),
    leave: () => RoomApiV2.leave(roomId, me.id),
    start: () => RoomApiV2.start(roomId),
    stop: () => RoomApiV2.stop(roomId),
    chooseAlias: (alias: string) => RoomApiV2.chooseAlias(roomId, me.id, alias),
    microphoneOn: () => RoomApiV2.unmute(roomId, me.id),
    microphoneOff: () => RoomApiV2.mute(roomId, me.id),
    cameraOn: () => RoomApiV2.cameraOn(roomId, me.id),
    cameraOff: () => RoomApiV2.cameraOff(roomId, me.id),
    screenShareOn: () => RoomApiV2.screenShareOn(roomId, me.id),
    screenShareOff: () => RoomApiV2.screenShareOff(roomId, me.id),
    sendMessage: (message: string) => RoomApiV2.chat(roomId, message),
    handUp: () => RoomApiV2.handUp(roomId, me.id),
    handDown: () => RoomApiV2.handDown(roomId, me.id),
    startSpeaking: () => RoomApiV2.startSpeaking(roomId, me.id),
    stopSpeaking: () => RoomApiV2.stopSpeaking(roomId, me.id),
    startTyping: () => RoomApiV2.stopTyping(roomId, me.id),
    stopTyping: () => RoomApiV2.stopTyping(roomId, me.id),
    changeToParticipant: () => RoomApiV2.changeRole(roomId, me.id, 'PARTICIPANT'),
    changeToSpectator: () => RoomApiV2.changeRole(roomId, me.id, 'SPECTATOR')
  } : {
    join: noop,
    leave: noop,
    start: noop,
    stop: noop,
    chooseAlias: noop,
    microphoneOn: noop,
    microphoneOff: noop,
    cameraOn: noop,
    cameraOff: noop,
    screenShareOn: noop,
    screenShareOff: noop,
    sendMessage: noop,
    handUp: noop,
    handDown: noop,
    startSpeaking: noop,
    stopSpeaking: noop,
    startTyping: noop,
    stopTyping: noop,
    changeToParticipant: noop,
    changeToSpectator: noop
  }

  // connect to the websocket on mount
  useEffect(() => {
    connect()

    // disconnect on unmount
    return () => {
      disconnect()
    }
  }, [])

  // (re-)subscribe to websocket topics on connect
  useEffect(() => {
    if(!socketConnected) return

    // subscribe to room updates
    const roomSubscription = subscribe(`/room/${roomId}`, onRoomUpdate)

    // get the chat history subscribe to chat updates
    RoomApiV2.chatHistory(roomId).then(setMessages)
    const chatSubscription = subscribe(`/room/${roomId}/chat`, onChatUpdate)

    // clean-up subscriptions
    return () => {
      roomSubscription?.unsubscribe()
      chatSubscription?.unsubscribe()
    }
  }, [socketConnected])

  // join meeting when in ROOM
  useEffect(() => {
    if(me?.screen === 'ROOM') {
      join()

      return () => {
        leave()
      }
    }
    else {
      leave()
    }
  }, [me?.screen])

  // set the meeting microphone
  useEffect(() => {
    setMicrophone(selectedMicrophone).then(() => {
      // turn the microphone off, if necessary
      if(!me?.microphone) microphoneOff()
    })

    // selecting NO_MICROPHONE changes to spectator and turns microphone off
    if(selectedMicrophone === NO_MICROPHONE) {
      actions.changeToSpectator().then(() => actions.microphoneOff())
    }
    // selecting a microphone changes to participant
    else {
      actions.changeToParticipant()
    }
  }, [selectedMicrophone])

  // turn meeting microphone off/on
  useEffect(() => {
    if(me?.microphone) microphoneOn()
    else microphoneOff()
  }, [me?.microphone])

  // set the meeting camera
  useEffect(() => {
    setCamera(selectedCamera).then(() => {
      // turn the camera off, if necessary
      if(!me?.camera) cameraOff()
    })

    // selecting NO_CAMERA also turns the camera off
    if(selectedCamera === NO_CAMERA) actions.cameraOff()

  }, [selectedCamera])

  // speaking detection
  useEffect(() => {
    if(me?.microphone && isSpeaking) actions.startSpeaking()
    else actions.stopSpeaking()
  }, [me?.microphone, isSpeaking])

  // turn meeting camera off/on
  useEffect(() => {
    if(me?.camera) cameraOn()
    else cameraOff()
  }, [me?.camera])

  return {
    connected: socketConnected || meetingConnected,
    error: socketError || meetingError,
    info: roomInfo,
    me,
    participants,
    audio,
    video,
    messages,
    ...actions
  }
}

export default useRoom